"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState, createContext, useContext, type FC, Component, type ReactNode, type ErrorInfo } from "react";
import { ThreadPrimitive, useAuiState } from "@assistant-ui/react";
import { Virtuoso, type VirtuosoHandle } from "react-virtuoso";
import { ReconnectingIndicator } from "@/components/assistant-ui/reconnectingIndicator";
import {
  UserMessage,
  AssistantMessage,
  EditComposer,
} from "./messages";

// ─── Context for exposing Virtuoso scroll state to siblings (e.g. ScrollToBottom) ─

interface VirtualizedMessagesContextValue {
  isAtBottom: boolean;
  scrollToBottom: () => void;
}

const VirtualizedMessagesContext = createContext<VirtualizedMessagesContextValue>({
  isAtBottom: true,
  scrollToBottom: () => {},
});

export const useVirtualizedMessages = () => useContext(VirtualizedMessagesContext);

// ─── Footer component (renders after all messages, scrolls with content) ─────

const VirtuosoFooter: FC = () => <ReconnectingIndicator />;

// ─── Provider wrapper (wrap around Thread's non-empty branch) ────────────────

export const VirtualizedMessagesProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const messagesLength = useAuiState(({ thread }) => thread.messages.length);
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);

  const scrollToBottom = useCallback(() => {
    virtuosoRef.current?.scrollToIndex({
      index: messagesLength - 1,
      align: "end",
      behavior: "smooth",
    });
  }, [messagesLength]);

  const contextValue = useMemo<VirtualizedMessagesContextValue>(
    () => ({ isAtBottom, scrollToBottom }),
    [isAtBottom, scrollToBottom],
  );

  return (
    <VirtualizedMessagesContext.Provider value={contextValue}>
      <div className="aui-thread-viewport relative flex flex-1 flex-col overflow-hidden px-4 pt-4">
        <VirtualizedMessagesList
          virtuosoRef={virtuosoRef}
          onAtBottomStateChange={setIsAtBottom}
        />
        {children}
      </div>
    </VirtualizedMessagesContext.Provider>
  );
};

// ─── Virtualized list (inner, receives ref from provider) ────────────────────

interface VirtualizedMessagesListProps {
  virtuosoRef: React.RefObject<VirtuosoHandle | null>;
  onAtBottomStateChange: (atBottom: boolean) => void;
}

const VirtualizedMessagesList: FC<VirtualizedMessagesListProps> = React.memo(({
  virtuosoRef,
  onAtBottomStateChange,
}) => {
  const messagesLength = useAuiState(({ thread }) => thread.messages.length);
  const remoteId = useAuiState((s) => s.threadListItem.remoteId);

  // One-shot scroll to absolute bottom on mount. Virtuoso's initialTopMostItemIndex
  // puts the last item at the TOP of the viewport; for long messages the user sees
  // the beginning, not the end. This effect fires once after Virtuoso settles and
  // scrolls to the true bottom (align: "end"). The component remounts on thread
  // switch via key={remoteId}, so this naturally fires once per navigation.
  const didInitialScroll = useRef(false);
  useEffect(() => {
    if (didInitialScroll.current || messagesLength === 0) return;
    const timer = setTimeout(() => {
      virtuosoRef.current?.scrollToIndex({
        index: messagesLength - 1,
        align: "end",
        behavior: "auto",
      });
      didInitialScroll.current = true;
    }, 50);
    return () => clearTimeout(timer);
  }, [messagesLength, virtuosoRef]);

  const messageComponents = useMemo(
    () => ({ UserMessage, EditComposer, AssistantMessage }),
    [],
  );

  const handleFollowOutput = useCallback(
    (atBottom: boolean): false | "smooth" => {
      return atBottom ? "smooth" : false;
    },
    [],
  );

  const virtuosoComponents = useMemo(() => ({ Footer: VirtuosoFooter }), []);

  if (messagesLength === 0) return null;

  return (
    <Virtuoso
      ref={virtuosoRef}
      key={remoteId ?? "local"}
      totalCount={messagesLength}
      initialTopMostItemIndex={messagesLength - 1}
      followOutput={handleFollowOutput}
      atBottomStateChange={onAtBottomStateChange}
      atBottomThreshold={10}
      defaultItemHeight={120}
      increaseViewportBy={{ top: 1500, bottom: 200 }}
      overscan={600}
      className="flex-1"
      components={virtuosoComponents}
      itemContent={(index) => (
        <StaleIndexBoundary index={index} messagesLength={messagesLength}>
          <ThreadPrimitive.MessageByIndex
            index={index}
            components={messageComponents}
          />
        </StaleIndexBoundary>
      )}
    />
  );
});

VirtualizedMessagesList.displayName = "VirtualizedMessagesList";

// ─── Stale-index boundary ────────────────────────────────────────────────────
// Virtuoso may briefly request rendering an index that no longer exists in the
// runtime's message list (e.g., during thread switch, edit, or remount).
// assistant-ui's MessageByIndexProvider throws synchronously via useSyncExternalStore
// in that case. This boundary catches the throw and renders nothing.
//
// IMPORTANT: No auto-reset via requestAnimationFrame — that causes an infinite
// oscillation (catch → reset → throw → catch …) which triggers React #185
// "Maximum update depth exceeded". Instead, the boundary resets only when:
//   1. The `index` prop changes (Virtuoso recycled the slot for a different index)
//   2. The `messagesLength` changes (message list grew/shrank — the stale index
//      may now be valid, or Virtuoso will unmount this slot entirely)

interface StaleIndexBoundaryProps {
  children: ReactNode;
  index: number;
  messagesLength: number;
}

interface StaleIndexBoundaryState {
  hasError: boolean;
  // Track the props at error time so we can reset when they change
  errorIndex: number;
  errorMessagesLength: number;
}

class StaleIndexBoundary extends Component<StaleIndexBoundaryProps, StaleIndexBoundaryState> {
  state: StaleIndexBoundaryState = { hasError: false, errorIndex: -1, errorMessagesLength: -1 };

  static getDerivedStateFromError(error: Error): Partial<StaleIndexBoundaryState> | null {
    if (error.message?.includes("out of bounds")) return { hasError: true };
    throw error; // re-throw non-index errors
  }

  static getDerivedStateFromProps(
    props: StaleIndexBoundaryProps,
    state: StaleIndexBoundaryState,
  ): Partial<StaleIndexBoundaryState> | null {
    if (!state.hasError) return null;
    // Reset if index changed (slot recycled) or messagesLength changed (data updated)
    if (props.index !== state.errorIndex || props.messagesLength !== state.errorMessagesLength) {
      return { hasError: false, errorIndex: -1, errorMessagesLength: -1 };
    }
    return null;
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    // Stamp the error context so getDerivedStateFromProps can compare
    this.setState({
      errorIndex: this.props.index,
      errorMessagesLength: this.props.messagesLength,
    });
    // Suppress telemetry noise — this is an expected transient during message list updates
    if (!error.message?.includes("out of bounds")) {
      throw error;
    }
  }

  render() {
    // Belt-and-suspenders: if the index is clearly out of bounds, don't even
    // attempt to render children (prevents the throw in useSyncExternalStore)
    if (this.props.index >= this.props.messagesLength) return null;
    if (this.state.hasError) return null;
    return this.props.children;
  }
}
